import { useEffect, useRef } from "react";
import { AGENTS } from "../lib/agents";
import { AgentCard } from "../components/AgentCard";
import { Navbar } from "../components/Navbar";
import { MicrosoftConnect } from "../components/MicrosoftConnect";
import { Toaster, toast } from "../components/ui/sonner";

export default function Dashboard() {
  const toastShown = useRef(false);

  useEffect(() => {
    if (toastShown.current) return;
    const params = new URLSearchParams(window.location.search);
    const msFlag = params.get("microsoft");
    if (msFlag === "connected") {
      toastShown.current = true;
      toast.success("Microsoft 365 connected successfully!");
      params.delete("microsoft");
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", newUrl);
    } else if (msFlag === "error") {
      toastShown.current = true;
      const reason = params.get("reason") || "Please try again.";
      toast.error(`Microsoft 365 connection failed: ${reason}`);
      params.delete("microsoft");
      params.delete("reason");
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  return (
    <div
      data-testid="dashboard-page"
      className="min-h-screen bg-brand text-brand-mint font-sans relative overflow-hidden"
    >
      {/* ambient mint glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-[600px] w-[1100px] rounded-full bg-brand-mint/20 blur-[140px]" />
        <div className="absolute top-[30%] -left-32 h-[420px] w-[420px] rounded-full bg-brand-mint/15 blur-[120px]" />
        <div className="absolute bottom-[-15%] right-[-10%] h-[520px] w-[680px] rounded-full bg-brand-mint/18 blur-[130px]" />
        <div className="absolute top-[55%] right-[20%] h-[260px] w-[260px] rounded-full bg-brand-mintBright/12 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(160 228 203) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <Navbar />

      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-20">
        <div className="text-center mb-14 md:mb-16">
          <img
            src="https://customer-assets.emergentagent.com/job_launch-agents/artifacts/09567i82_launchhub.png"
            alt="LaunchHub"
            className="h-24 w-24 md:h-28 md:w-28 mx-auto mb-6 rounded-full object-cover ring-2 ring-brand-mint/30 shadow-2xl shadow-brand-deeper/40"
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-deep/60 border border-brand-mint/20 text-xs font-medium text-brand-mint/80 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-mint" />
            Ignite Excellence
          </div>
          <h1
            data-testid="dashboard-heading"
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-mint"
          >
            Your{" "}
            <span className="text-brand-mintBright">
              AI Launch Agents
            </span>
          </h1>
          <p
            data-testid="dashboard-subheading"
            className="mt-4 text-base md:text-lg text-brand-mint/75 max-w-2xl mx-auto"
          >
            Select an agent to begin your session.
          </p>
        </div>

        <div
          data-testid="agent-grid"
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {AGENTS.map((agent, idx) => (
            <AgentCard key={agent.id} agent={agent} index={idx} />
          ))}
        </div>

        <div className="mt-8 md:mt-10">
          <MicrosoftConnect />
        </div>

        <p className="mt-14 text-center text-xs text-brand-mint/50 tracking-wide">
          Powered by Claude Opus 4.5 · Built for pharmaceutical &amp; biotech launch teams
        </p>
      </main>

      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
}
